import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { capitalize } from "components/Resume/ResumeEleven/common/capitalize";
import { handleClick } from "lib/utils/navigator";

export const TemplateNineteenPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return  (
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      cursor:"pointer"
      // ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: "black",
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
        {themeColor && (
          <View
            style={{
              height: "1.25pt",
              width: "100%",
              backgroundColor: "white",
              marginRight: spacing["3.5"],
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          />
        )}
      </View>
    )}
    {children}
  </div>
)};



// export const TemplateNineteenPDFSection = ({
//   themeColor,
//   heading,
//   style = {},
//   children,
// }: {
//   themeColor?: string;
//   heading?: string;
//   style?: Style;
//   children: React.ReactNode;
// }) => (
//   <View
//     style={{
//       ...styles.flexCol,
//       gap: spacing["2"],
//       marginTop: spacing["5"],
//       ...style,
//     }}
//   >
//     {heading && (
//       <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
//         <Text
//           style={{
//             fontWeight: "bold",
//             letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
//             color: "white",
//           }}
//           debug={DEBUG_RESUME_PDF_FLAG}
//         >
//           {heading}
//         </Text>
//         {themeColor && (
//           <View
//             style={{
//               height: "1.25pt",
//               width: "100%",
//               backgroundColor: "white",
//               marginRight: spacing["3.5"],
//             }}
//             debug={DEBUG_RESUME_PDF_FLAG}
//           />
//         )}
//       </View>
//     )}
//     {children}
//   </View>
// );

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
}: {
  items: string[];
}) => {
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

export const ResumePDFDescription = ({ items}: { items: string[] }) => {
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

// Re-export common components
export { ResumePDFText } from "components/Resume/ResumePDF/common";

// Export optimized section components
export { TemplateNineteenSectionWithSubsections, TemplateNineteenSection } from "./sections";
